import React, { useState, useEffect } from 'react';
import api from '../config/api';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { geocodeAddress, getCurrentLocation, reverseGeocode } from '../utils/maps';
import AutocompleteInput from '../components/AutocompleteInput';
import { FaCrosshairs } from 'react-icons/fa';
import MapContainer from '../components/MapContainer';

const CreateTrip = () => {
  const [formData, setFormData] = useState({
    origin: {
      address: '',
      coordinates: { lat: 0, lng: 0 }
    },
    destination: {
      address: '',
      coordinates: { lat: 0, lng: 0 }
    },
    departureTime: '',
    price: '',
    availableSeats: 1,
    description: '',
    preferences: {
      smoking: false,
      music: false,
      chat: true
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState({ origin: false, destination: false });
  const [waypoints, setWaypoints] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Auto-fill if search params exist (e.g., from trip search)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const origin = params.get('origin');
    const destination = params.get('destination');
    const date = params.get('date');

    if (origin) {
      const [lat, lng] = origin.split(',').map(Number);
      setFormData(prev => ({
        ...prev,
        origin: {
          address: '',
          coordinates: { lat, lng }
        }
      }));
    }

    if (destination) {
      const [lat, lng] = destination.split(',').map(Number);
      setFormData(prev => ({
        ...prev,
        destination: {
          address: '',
          coordinates: { lat, lng }
        }
      }));
    }

    if (date) {
      const dateObj = new Date(date);
      // Format to ISO string for datetime-local input
      const localDate = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setFormData(prev => ({ ...prev, departureTime: localDate }));
    }
  }, [location.search]);

  // Set default seats based on vehicle type
  useEffect(() => {
    if (user?.vehicleInfo) {
      const vehicleType = user.vehicleInfo.type;
      const isFourWheeler = ['car', 'sedan', 'hatchback', 'suv'].includes(vehicleType);
      if (isFourWheeler) {
        const defaultSeats = user.vehicleInfo.seats || 4;
        setFormData(prev => ({
          ...prev,
          availableSeats: defaultSeats
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          availableSeats: 1
        }));
      }
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else if (name.startsWith('preferences.')) {
      const prefKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          [prefKey]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Check profile completion - don't navigate, just track state
  const [profileStatus, setProfileStatus] = useState({
    isComplete: false,
    missingFields: [],
    isVerified: false
  });

  useEffect(() => {
    if (user && user.role === 'rider') {
      const hasVehicleInfo = !!(user.vehicleInfo && user.vehicleInfo.type && user.vehicleInfo.model && user.vehicleInfo.plateNumber);
      const hasBankDetails = !!(user.bankDetails && user.bankDetails.accountNumber);
      const isVerified = user.verificationStatus === 'verified';
      
      const missingFields = [];
      if (!hasVehicleInfo) missingFields.push('Vehicle Details');
      if (!hasBankDetails) missingFields.push('Bank Details');
      
      setProfileStatus({
        isComplete: hasVehicleInfo && hasBankDetails,
        missingFields,
        isVerified
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check profile completion before submitting
    if (user && user.role === 'rider') {
      const hasVehicleInfo = !!(user.vehicleInfo && user.vehicleInfo.type && user.vehicleInfo.model && user.vehicleInfo.plateNumber);
      const hasBankDetails = !!(user.bankDetails && user.bankDetails.accountNumber);
      const isVerified = user.verificationStatus === 'verified';
      
      if (!hasVehicleInfo || !hasBankDetails) {
        toast.error('Please complete your profile (Vehicle Details and Bank Details) before creating a trip', {
          duration: 5000
        });
        navigate('/profile');
        return;
      }
      
      if (!isVerified) {
        toast.error('Your profile is pending verification. Please wait for admin verification before creating trips.', {
          duration: 5000
        });
        navigate('/profile');
        return;
      }
    }
    
    setLoading(true);

    try {
      // Validation
      if (!formData.origin.address || !formData.destination.address) {
        throw new Error('Origin and destination addresses are required');
      }

      if (!formData.departureTime) {
        throw new Error('Departure time is required');
      }

      if (!formData.price || parseFloat(formData.price) <= 0) {
        throw new Error('Valid price is required');
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Use coordinates if already available from autocomplete, otherwise geocode
      let originGeo, destGeo;
      
      if (formData.origin.coordinates.lat && formData.origin.coordinates.lng) {
        // Coordinates already available from autocomplete
        originGeo = {
          lat: formData.origin.coordinates.lat,
          lng: formData.origin.coordinates.lng,
          formatted_address: formData.origin.address
        };
      } else {
        // Need to geocode
        originGeo = await geocodeAddress(formData.origin.address);
      }

      if (formData.destination.coordinates.lat && formData.destination.coordinates.lng) {
        // Coordinates already available from autocomplete
        destGeo = {
          lat: formData.destination.coordinates.lat,
          lng: formData.destination.coordinates.lng,
          formatted_address: formData.destination.address
        };
      } else {
        // Need to geocode
        destGeo = await geocodeAddress(formData.destination.address);
      }

      // Determine max seats based on vehicle type
      const vehicleType = user?.vehicleInfo?.type;
      const maxSeats = ['car', 'sedan', 'hatchback', 'suv'].includes(vehicleType) 
        ? (user?.vehicleInfo?.seats || 10)
        : 1;
      
      const availableSeats = Math.min(
        Math.max(parseInt(formData.availableSeats) || 1, 1),
        maxSeats
      );

      // Validate and prepare waypoints
      const formattedWaypoints = [];
      const waypointsArray = Array.isArray(waypoints) ? waypoints : [];
      for (let i = 0; i < waypointsArray.length; i++) {
        const wp = waypointsArray[i];
        if (!wp.address || !wp.coordinates.lat || !wp.coordinates.lng) {
          // Try to geocode if coordinates are missing
          if (wp.address) {
            try {
              const wpGeo = await geocodeAddress(wp.address);
              formattedWaypoints.push({
                address: wpGeo.formatted_address || wp.address,
                coordinates: {
                  lat: wpGeo.lat,
                  lng: wpGeo.lng
                },
                stopover: true,
                order: i + 1
              });
            } catch (error) {
              throw new Error(`Invalid stop ${i + 1}: ${wp.address}. Please select from suggestions.`);
            }
          } else {
            throw new Error(`Stop ${i + 1} is incomplete. Please enter a valid address.`);
          }
        } else {
          formattedWaypoints.push({
            address: wp.address,
            coordinates: wp.coordinates,
            stopover: true,
            order: i + 1
          });
        }
      }

      const tripData = {
        origin: {
          address: originGeo.formatted_address || formData.origin.address,
          coordinates: {
            lat: originGeo.lat,
            lng: originGeo.lng
          }
        },
        destination: {
          address: destGeo.formatted_address || formData.destination.address,
          coordinates: {
            lat: destGeo.lat,
            lng: destGeo.lng
          }
        },
        departureTime: formData.departureTime,
        price: parseFloat(formData.price),
        availableSeats: availableSeats,
        description: formData.description,
        preferences: formData.preferences,
        waypoints: formattedWaypoints
      };

      await api.post('/api/trips', tripData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Trip created successfully!');
      navigate('/my-trips');
      
    } catch (err) {
      console.error('Create trip error:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        toast.error('Session expired. Please log in again.');
        navigate('/login');
      } else if (err.response?.status === 403) {
        toast.error('Only riders can create trips. Please update your profile.');
      } else {
        const msg = err.message || 
                   err.response?.data?.message || 
                   err.response?.data?.errors?.[0]?.msg || 
                   'Failed to create trip';
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUseCurrentLocation = async (field) => {
    setLoadingLocation(prev => ({ ...prev, [field]: true }));
    try {
      // Get current location
      const location = await getCurrentLocation();
      
      // Reverse geocode to get address
      const addressData = await reverseGeocode(location.lat, location.lng);
      
      // Update form data
      if (field === 'origin') {
        setFormData(prev => ({
          ...prev,
          origin: {
            address: addressData.address,
            coordinates: { lat: addressData.lat, lng: addressData.lng }
          }
        }));
        toast.success('Current location set as origin');
      } else {
        setFormData(prev => ({
          ...prev,
          destination: {
            address: addressData.address,
            coordinates: { lat: addressData.lat, lng: addressData.lng }
          }
        }));
        toast.success('Current location set as destination');
      }
    } catch (error) {
      console.error('Error getting current location:', error);
      if (error.message.includes('denied')) {
        toast.error('Location access denied. Please enable location permissions in your browser settings.');
      } else if (error.message.includes('timeout')) {
        toast.error('Location request timed out. Please try again.');
      } else {
        toast.error('Failed to get current location. Please try again.');
      }
    } finally {
      setLoadingLocation(prev => ({ ...prev, [field]: false }));
    }
  };

  const canCreateTrip = profileStatus.isComplete && profileStatus.isVerified;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Create New Trip</h1>
          
          {/* Profile Completion Warning */}
          {user?.role === 'rider' && (!profileStatus.isComplete || !profileStatus.isVerified) && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Profile Completion Required
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    {profileStatus.missingFields.length > 0 && (
                      <p className="mb-2">
                        Please complete: <strong>{profileStatus.missingFields.join(' and ')}</strong>
                      </p>
                    )}
                    {!profileStatus.isVerified && profileStatus.isComplete && (
                      <p className="mb-2">
                        Your profile is pending admin verification. You'll be able to create trips once verified.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate('/profile')}
                      className="font-medium underline hover:text-yellow-900"
                    >
                      Go to Profile →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-white p-6 rounded-lg shadow-md relative z-0">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Origin */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From (Pickup Location)
                </label>
                <div className="relative">
                  <AutocompleteInput
                    name="origin.address"
                    value={formData.origin.address}
                    onChange={handleChange}
                    onPlaceSelect={(place) => {
                      setFormData(prev => ({
                        ...prev,
                        origin: {
                          address: place.address,
                          coordinates: place.coordinates
                        }
                      }));
                    }}
                    placeholder="Start typing to see suggestions..."
                    required
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => handleUseCurrentLocation('origin')}
                    disabled={loadingLocation.origin}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Use Current Location"
                  >
                    {loadingLocation.origin ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    ) : (
                      <FaCrosshairs />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Use the crosshair icon to set your current location
                </p>
              </div>
              
              {/* Destination */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To (Destination)
                </label>
                <div className="relative">
                  <AutocompleteInput
                    name="destination.address"
                    value={formData.destination.address}
                    onChange={handleChange}
                    onPlaceSelect={(place) => {
                      setFormData(prev => ({
                        ...prev,
                        destination: {
                          address: place.address,
                          coordinates: place.coordinates
                        }
                      }));
                    }}
                    placeholder="Start typing to see suggestions..."
                    required
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => handleUseCurrentLocation('destination')}
                    disabled={loadingLocation.destination}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Use Current Location"
                  >
                    {loadingLocation.destination ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    ) : (
                      <FaCrosshairs />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Use the crosshair icon to set your current location
                </p>
              </div>

              {/* Waypoints/Stops */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Stops/Waypoints (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setWaypoints([...waypoints, { address: '', coordinates: { lat: 0, lng: 0 } }]);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    + Add Stop
                  </button>
                </div>
                {waypoints.length > 0 && (
                  <div className="space-y-2 mb-2">
                    {waypoints.map((waypoint, index) => (
                      <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-600">Stop {index + 1}</span>
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newWaypoints = [...waypoints];
                                  [newWaypoints[index - 1], newWaypoints[index]] = [newWaypoints[index], newWaypoints[index - 1]];
                                  setWaypoints(newWaypoints);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800"
                                title="Move up"
                              >
                                ↑
                              </button>
                            )}
                            {index < waypoints.length - 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newWaypoints = [...waypoints];
                                  [newWaypoints[index], newWaypoints[index + 1]] = [newWaypoints[index + 1], newWaypoints[index]];
                                  setWaypoints(newWaypoints);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800"
                                title="Move down"
                              >
                                ↓
                              </button>
                            )}
                          </div>
                          <AutocompleteInput
                            value={waypoint.address}
                            onChange={(e) => {
                              const newWaypoints = [...waypoints];
                              newWaypoints[index].address = e.target.value;
                              setWaypoints(newWaypoints);
                            }}
                            onPlaceSelect={(place) => {
                              const newWaypoints = [...waypoints];
                              newWaypoints[index] = {
                                address: place.address,
                                coordinates: place.coordinates
                              };
                              setWaypoints(newWaypoints);
                            }}
                            placeholder="Enter stop location..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setWaypoints(waypoints.filter((_, i) => i !== index));
                          }}
                          className="text-red-600 hover:text-red-800 font-medium text-lg px-2"
                          title="Remove stop"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  💡 Add stops along your route. Passengers can see these stops on the map.
                </p>
              </div>
              
              {/* Departure Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Departure Time
                </label>
                <input
                  type="datetime-local"
                  name="departureTime"
                  value={formData.departureTime}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price per Seat (₹ INR)
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="1"
                  step="1"
                  placeholder="Enter price"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Available Seats - Only for four-wheelers */}
              {user?.vehicleInfo && ['car', 'sedan', 'hatchback', 'suv'].includes(user.vehicleInfo.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Seats (Max: {user.vehicleInfo.seats || 10})
                  </label>
                  <input
                    type="number"
                    name="availableSeats"
                    value={formData.availableSeats}
                    onChange={handleChange}
                    required
                    min="1"
                    max={user.vehicleInfo.seats || 10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your vehicle has {user.vehicleInfo.seats || 10} passenger seats
                  </p>
                </div>
              )}
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  maxLength="500"
                  placeholder="Tell passengers about your trip..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500</p>
              </div>
              
              {/* Route Preview */}
              {(formData.origin.coordinates.lat && formData.origin.coordinates.lng && 
                formData.destination.coordinates.lat && formData.destination.coordinates.lng) && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Route Preview</h3>
                  <div className="rounded-lg overflow-hidden border border-gray-200">
                    <MapContainer
                      origin={formData.origin.coordinates}
                      destination={formData.destination.coordinates}
                      waypoints={waypoints.filter(wp => wp.coordinates.lat && wp.coordinates.lng)}
                      height="300px"
                      showRoute={true}
                    />
                  </div>
                  {waypoints.length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                      <p>Route with {waypoints.length} stop{waypoints.length !== 1 ? 's' : ''}:</p>
                      <ol className="list-decimal list-inside mt-1 space-y-1">
                        <li>{formData.origin.address || 'Origin'}</li>
                        {waypoints.map((wp, idx) => (
                          <li key={idx}>{wp.address || `Stop ${idx + 1}`}</li>
                        ))}
                        <li>{formData.destination.address || 'Destination'}</li>
                      </ol>
                    </div>
                  )}
                </div>
              )}

              {/* Preferences */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Trip Preferences</h3>
                
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="preferences.smoking"
                      checked={formData.preferences.smoking}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Smoking allowed</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="preferences.music"
                      checked={formData.preferences.music}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Music on</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="preferences.chat"
                      checked={formData.preferences.chat}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Chat friendly</span>
                  </label>
                </div>
              </div>
              
               <button
                 type="submit"
                 disabled={loading || !canCreateTrip}
                 className={`w-full py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                   canCreateTrip
                     ? 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
                     : 'bg-gray-400 text-white cursor-not-allowed'
                 }`}
               >
                 {loading ? 'Creating Trip...' : canCreateTrip ? 'Create Trip' : 'Complete Profile to Create Trip'}
               </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTrip;